import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSend, FiLoader, FiUser, FiSettings, FiRefreshCw } from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import toast from 'react-hot-toast';
import { api } from '../utils/api';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  isStreaming?: boolean;
}

interface AssistantChatTestProps {
  assistantData: any;
  onClose: () => void;
}

const AssistantChatTest: React.FC<AssistantChatTestProps> = ({
  assistantData,
  onClose
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('gemini');
  const [availableProviders, setAvailableProviders] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<string>('');

  useEffect(() => {
    // Load available LLM providers
    loadProviders();
    
    // Add welcome message
    setMessages([{
      id: '1',
      content: `Hello! I'm ${assistantData.name}. ${assistantData.purpose || 'How can I help you today?'}`,
      sender: 'assistant',
      timestamp: new Date()
    }]);
  }, [assistantData]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadProviders = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.log('No access token available');
        // Set default provider without API call
        setAvailableProviders([
          { name: 'gemini', displayName: 'Gemini', enabled: true },
          { name: 'openai', displayName: 'OpenAI', enabled: false },
          { name: 'mistral', displayName: 'Mistral', enabled: false },
        ]);
        setSelectedProvider('gemini');
        return;
      }
      
      const response = await api.get('/llm/providers');
      
      if (response.data.success) {
        const providers = response.data.providers || [];
        const enabledProviders = Array.isArray(providers) ? providers.filter((p: any) => p.enabled) : [providers];
        setAvailableProviders(enabledProviders);
        setSelectedProvider(response.data.defaultProvider || enabledProviders[0]?.name || 'gemini');
      }
    } catch (error) {
      console.error('Failed to load providers:', error);
      // Set default provider on error
      setAvailableProviders([{ name: 'gemini', displayName: 'Gemini', enabled: true }]);
      setSelectedProvider('gemini');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Build system prompt based on assistant data
      const systemPrompt = `You are ${assistantData.name}, an AI assistant. 
        ${assistantData.purpose ? `Your purpose is: ${assistantData.purpose}` : ''}
        ${assistantData.description ? `Description: ${assistantData.description}` : ''}
        Respond helpfully and in character.`;

      // Build conversation history for context
      const conversationHistory = messages.map(msg => 
        `${msg.sender === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
      ).join('\n');

      // Create the full prompt with conversation history
      const fullPrompt = conversationHistory 
        ? `Previous conversation:\n${conversationHistory}\n\nUser: ${inputMessage}\nAssistant:`
        : inputMessage;

      // Call the LLM API - using public chat endpoint for testing
      const response = await api.post('/llm/chat/message', {
        message: inputMessage,
        assistantId: assistantData.id,
        systemPrompt: systemPrompt,
        model: undefined,
        temperature: 0.7,
        maxTokens: 500,
        provider: selectedProvider
      });

      const result = response.data;
      
      if (result.success) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: result.response,
          sender: 'assistant',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error(result.error || 'Failed to generate response');
      }
      
      setIsStreaming(false);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get response. Please check API configuration.');
      
      // Remove streaming message if error occurred
      setMessages(prev => prev.filter(msg => !msg.isStreaming));
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const clearChat = () => {
    setMessages([{
      id: '1',
      content: `Hello! I'm ${assistantData.name}. ${assistantData.purpose || 'How can I help you today?'}`,
      sender: 'assistant',
      timestamp: new Date()
    }]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-slate-900 rounded-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <BiBot className="text-white" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{assistantData.name}</h3>
              <p className="text-sm text-gray-400">Test Chat - {selectedProvider}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Provider Selection */}
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
            >
              {availableProviders.map(provider => (
                <option key={provider.name} value={provider.name}>
                  {provider.name}
                </option>
              ))}
            </select>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={clearChat}
              className="p-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <FiRefreshCw size={16} />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Close
            </motion.button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`flex gap-3 ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.sender === 'assistant' && (
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BiBot className="text-white" size={16} />
                  </div>
                )}
                
                <div
                  className={`max-w-[70%] p-3 rounded-lg ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-800 text-gray-100'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  {message.isStreaming && (
                    <motion.div
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="inline-block w-2 h-4 bg-current ml-1"
                    />
                  )}
                  <p className="text-xs opacity-70 mt-1">
                    {message.timestamp.toLocaleTimeString()}
                  </p>
                </div>
                
                {message.sender === 'user' && (
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FiUser className="text-white" size={16} />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-gray-400 resize-none"
                rows={1}
                style={{ minHeight: '44px', maxHeight: '120px' }}
                disabled={isLoading}
              />
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="p-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center"
            >
              {isLoading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <FiLoader size={18} />
                </motion.div>
              ) : (
                <FiSend size={18} />
              )}
            </motion.button>
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AssistantChatTest;